/**
 * Hand scored: the human moment typed on the felt, the record written on paper.
 *
 * The ledger lines come from the engine's `HandResult.lines` — cause before
 * total, which is the one thing Balatro's scoring sequence gets right and most
 * card apps do not. The last trick lies flat in the hand band as a record.
 */
import { SuitText, plusMinus, signed } from './atoms'
import { CardFace } from './Card'
import type { TableView } from './view'
import { useSettings } from '../settings/settings'

export function HandSummary({
  view,
  onReady,
  onReview,
  onFairness,
  locked,
}: {
  view: TableView
  onReady: () => void
  onReview: () => void
  /** The hand summary is where "that deal was rigged" gets said, so the check
   *  for *this* hand is one tap from here rather than only from the match end. */
  onFairness?: () => void
  locked: boolean
}): JSX.Element {
  const [settings] = useSettings()
  const hand = view.hand
  const result = hand?.result ?? null
  const headline = view.gameUi.handResult(view)
  const contractLabel = lastContractLabel(view)
  // The deal usually moves on one seat; a game with its own rule says so
  // (Sixty-six: the hand's winner deals).
  const nextSeat = view.gameUi.nextDealer?.(view) ?? ((hand?.dealer ?? 0) + 1) % view.seatCount
  const nextDeal = nextSeat === view.mySeat ? 'You deal' : `${view.name(nextSeat)} deals`
  const readyForNext = view.match?.readyForNext ?? []
  const alreadyReady = view.mySeat !== null && readyForNext.includes(view.mySeat)
  const lastTrick = view.hand?.tricks.filter((t) => t.winner !== null).slice(-1)[0] ?? null
  // A hand thrown in for want of a bid reaches this screen with no result and
  // so no ledger lines. The pad still prints, because the running totals are
  // the reassuring part — but it has to say *why* it is empty, or an unchanged
  // scorepad reads as a scorepad that failed to update.
  const lines = result?.lines ?? []

  return (
    <>
      <section className="felt">
        <div className="result">
          <div className="headline">
            <h1>{headline?.headline ?? 'Hand scored'}</h1>
            <p>{headline?.sentence}</p>
          </div>
          <div className="pad">
            <div className="pad-row">
              <h2>Hand {(hand?.index ?? 0) + 1}</h2>
              <span className="k">
                {contractLabel}
                {contractorName(view) ? <>&nbsp;by {contractorName(view)}</> : null}
              </span>
            </div>
            {lines.map((line, i) => (
              <div className="pad-row ledger" key={i}>
                <span className="k">
                  <b>{line.team === view.myTeam ? 'Us' : 'Them'}</b>{' '}
                  <SuitText text={stripTeamWord(line.text)} />
                </span>
                <span className={`v${line.points < 0 ? ' minus' : ''}`}>{plusMinus(line.points)}</span>
              </div>
            ))}
            {lines.length === 0 ? (
              <div className="pad-row">
                <span className="k made">Nobody bid, so nobody scores</span>
                <span className="v made">0</span>
              </div>
            ) : null}
            <div className="pad-row total">
              <span className="k">
                <b>Us</b>
              </span>
              <span className={`v${view.scores.us < 0 ? ' minus' : ''}`}>{signed(view.scores.us)}</span>
            </div>
            <div className="pad-row">
              <span className="k">
                <b>Them</b>
              </span>
              <span className={`v${view.scores.them < 0 ? ' minus' : ''}`}>{signed(view.scores.them)}</span>
            </div>
          </div>
          {/* The button says what pressing it does, and the line under it says
              everything else. It used to read `Next hand, Cara deals`, which
              is a *statement about the next hand* — a caption, not a control —
              so there was nothing to tell a player that the table was waiting
              on them to press it. The fact it was carrying (who deals next) is
              real and worth keeping, so it moved to where facts go. */}
          <div className="result-actions">
            <button className="btn btn-primary btn-block" onClick={onReady} disabled={locked || alreadyReady}>
              {alreadyReady
                ? `Waiting for the others · ${readyForNext.length} of ${view.seatCount} ready`
                : "I'm ready"}
            </button>
            <div className="cta-note">
              {alreadyReady
                ? `${nextDeal} as soon as everyone has pressed it.`
                : `${nextDeal} the next hand, once everyone is ready.`}
            </div>
            <button className="btn btn-quiet" onClick={onReview}>
              Review the hand
            </button>
            {onFairness ? (
              <button className="fair" onClick={onFairness}>
                Check how this hand was shuffled
              </button>
            ) : null}
          </div>
        </div>
      </section>
      <section className="hand-band">
        <div className="hint" style={{ justifyContent: 'center' }}>
          <div className="hint-text muted">
            {lastTrick
              ? `Last trick, taken by ${view.name(lastTrick.winner ?? null)}`
              : view.hand?.phase === 'abandoned'
                ? 'No tricks were played'
                : 'The hand is over'}
          </div>
        </div>
        <div className="hand last">
          {(lastTrick?.plays ?? []).map((play) => {
            const marks = view.gameUi.cardMarks(view, play.card)
            return (
              <CardFace
                key={play.seat}
                card={play.card}
                deck={settings.deck}
                trump={marks.trump}
                stamp={marks.stamp}
              />
            )
          })}
        </div>
      </section>
    </>
  )
}

/** The contract this hand played under, labelled by the game's own module. */
function lastContractLabel(view: TableView): React.ReactNode {
  const summary = view.match?.handHistory.find((h) => h.index === view.hand?.index)
  if (summary) return view.gameUi.contractLabelNode(summary.contractLabel)
  const reading = view.gameUi.contractReading(view)
  return reading?.node ?? null
}

function contractorName(view: TableView): string | null {
  const summary = view.match?.handHistory.find((h) => h.index === view.hand?.index)
  if (summary && summary.contractorSeat !== null) return view.name(summary.contractorSeat)
  return null
}

/** The ledger already reads `Us`/`Them` in bold; a module that repeats the word
 *  in its own line text would otherwise print it twice. */
function stripTeamWord(text: string): string {
  return text.replace(/^(us|them|we|they)\s+/i, '')
}
